// src/api/handleApiResponse.ts
// Generic API response handler for { success, data, message } structure
// Includes utilities for handling nested data structures

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ApiResponse<T = any> = {
  success: boolean;
  data: T;
  message: string;
  pagination?: Pagination;
  errors?: string[]; // Validation errors array from backend
  statusCode?: number; // HTTP status code
  duplicateRequest?: boolean; // Idempotency duplicate flag
};

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: Pagination;
}

export interface NestedApiResponse<T> {
  success: boolean;
  data: {
    data: T;
    pagination?: Pagination;
  };
  message?: string;
}

export interface DirectApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Union type for all possible API response formats
export type FlexibleApiResponse<T> =
  | NestedApiResponse<T>
  | DirectApiResponse<T>;

// ============================================================================
// CORE API RESPONSE HANDLER
// ============================================================================

/**
 * Handles API responses and normalizes errors.
 * Supports backend validation error format with errors array.
 * @param promise Axios/fetch promise
 * @returns ApiResponse<T>
 */
export async function handleApiResponse<T>(
  promise: Promise<any>
): Promise<ApiResponse<T>> {
  try {
    const response = await promise;
    // If using axios, data is in response.data
    const api = response?.data || response;
    if (typeof api.success === "boolean") {
      return {
        success: api.success,
        data: api.data,
        message: api.message || "",
        pagination: api.pagination,
        errors: api.errors, // Extract validation errors array
        statusCode: api.statusCode, // Extract status code
        duplicateRequest: api.duplicateRequest,
      };
    }
    // Fallback: treat as success if no explicit flag
    return {
      success: true,
      data: api.data ?? api,
      message: api.message ?? "",
    };
  } catch (error: any) {
    // Axios error: error.response?.data
    const api = error?.response?.data;
    if (api && typeof api.success === "boolean") {
      return {
        success: api.success,
        data: api.data || (null as unknown as T),
        message: api.message || "Unknown error",
        errors: api.errors, // Extract validation errors array
        statusCode: api.statusCode || error?.response?.status, // Extract status code
        duplicateRequest: api.duplicateRequest,
      };
    }
    return {
      success: false,
      data: null as unknown as T,
      message: api?.message || error?.message || "Unknown error",
      errors: api?.errors,
      statusCode: error?.response?.status,
    };
  }
}

// ============================================================================
// NESTED DATA EXTRACTION UTILITIES
// ============================================================================

/**
 * Safely extract array data from API response with nested structure
 *
 * Handles these response formats:
 * 1. { data: { data: [...], pagination: {...} } } - Nested with pagination
 * 2. { data: [...] } - Direct array
 * 3. [...] - Raw array
 *
 * @param response - API response object
 * @returns Array of data items or empty array
 *
 * @example
 * const response = await getProducts();
 * const products = extractArrayData<Product>(response.data);
 */
export function extractArrayData<T>(response: any): T[] {
  const responseData = response?.data || response;

  // Check for nested products structure (data.products)
  if (Array.isArray(responseData?.products)) {
    return responseData.products;
  }

  // Check for nested data structure (data.data)
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  // Check for direct array
  if (Array.isArray(responseData)) {
    return responseData;
  }

  // Fallback to empty array
  return [];
}

/**
 * Extract pagination metadata from API response
 *
 * @param response - API response object
 * @returns Pagination object or null if not present
 *
 * @example
 * const response = await getProducts();
 * const pagination = extractPagination(response.data);
 * if (pagination) {
 *   console.log(`Page ${pagination.page} of ${pagination.totalPages}`);
 * }
 */
export function extractPagination(response: any): Pagination | null {
  const responseData = response?.data || response;
  return responseData?.pagination || null;
}

/**
 * Extract single object data from API response with nested structure
 *
 * Handles these response formats:
 * 1. { data: { data: {...} } } - Nested format
 * 2. { data: {...} } - Direct object
 *
 * @param response - API response object
 * @returns Data object or null
 *
 * @example
 * const response = await getUserById(userId);
 * const user = extractObjectData<User>(response.data);
 */
export function extractObjectData<T>(response: any): T | null {
  const responseData = response?.data || response;

  // Check for nested data structure (data.data)
  if (
    responseData?.data &&
    typeof responseData.data === "object" &&
    !Array.isArray(responseData.data)
  ) {
    return responseData.data;
  }

  // Check for direct object (not array)
  if (
    responseData &&
    typeof responseData === "object" &&
    !Array.isArray(responseData)
  ) {
    return responseData;
  }

  return null;
}

/**
 * Extract both data and pagination in one call
 *
 * @param response - API response object
 * @returns Object with data array and pagination (if present)
 *
 * @example
 * const response = await getProducts();
 * const { data: products, pagination } = extractDataWithPagination<Product>(response.data);
 */
export function extractDataWithPagination<T>(response: any): {
  data: T[];
  pagination: Pagination | null;
} {
  return {
    data: extractArrayData<T>(response),
    pagination: extractPagination(response),
  };
}

/**
 * Type guard to check if response is paginated
 */
export function isPaginatedResponse(response: any): boolean {
  const responseData = response?.data || response;
  return !!responseData?.pagination;
}

/**
 * Log API response structure for debugging
 *
 * @param label - Label for the log
 * @param response - API response object
 *
 * @example
 * const response = await getProducts();
 * logResponseStructure("Products API", response.data);
 */
export function logResponseStructure(label: string, response: any): void {
  const responseData = response?.data || response;

  console.log(`📦 ${label} response structure:`, {
    hasData: !!responseData,
    hasNestedData: !!responseData?.data,
    dataType: Array.isArray(responseData?.data)
      ? "array"
      : typeof responseData?.data,
    isDirectArray: Array.isArray(responseData),
    itemsCount: Array.isArray(responseData?.data)
      ? responseData.data.length
      : Array.isArray(responseData)
      ? responseData.length
      : "N/A",
    hasPagination: !!responseData?.pagination,
  });
}
